"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { BrowserProvider, getAddress, type Eip1193Provider as EthersEip1193Provider } from "ethers";
import { WagmiProvider, useAccount, useConnect, useDisconnect, useReconnect } from 'wagmi';
import { coinbaseConnector, injectedConnector, metaMaskInjectedConnector, walletConnectConnector, wagmiConfig } from './wagmi/config';

type JsonRpcRequest = { method: string; params?: unknown[] | Record<string, unknown> };

export type Eip1193Provider = {
  request: (req: JsonRpcRequest) => Promise<unknown>;
  on?: (event: string, listener: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, listener: (...args: unknown[]) => void) => void;
  disconnect?: () => void | Promise<void>;
  close?: () => void;
  isMetaMask?: boolean;
  isCoinbaseWallet?: boolean;
  isTrust?: boolean;
  isTrustWallet?: boolean;
  providers?: unknown[];
};

// Raw shape of an injected provider before we've validated it implements
// the EIP-1193 interface (used while probing window.ethereum / .providers).
type RawInjectedProvider = {
  request?: unknown;
  providers?: unknown[];
  isMetaMask?: boolean;
  isCoinbaseWallet?: boolean;
  isTrust?: boolean;
  isTrustWallet?: boolean;
};

type ProviderType = "injected" | "coinbase";

type EvmWalletContextValue = {
  address: string | null;
  isConnected: boolean;
  isConnecting: boolean;
  isInitialized: boolean;
  error: string | null;
  providerType: ProviderType | null;
  provider: Eip1193Provider | null;
  connectMetaMask: () => Promise<void>;
  connectTrustWallet: () => Promise<void>;
  connectBifrost: () => Promise<void>;
  connectCoinbase: () => Promise<void>;
  connectUniswapWallet: () => Promise<void>;
  reconnect: (opts?: { requestAccountsIfNeeded?: boolean }) => Promise<boolean>;
  disconnect: () => Promise<void>;
  getSigner: () => Promise<Awaited<ReturnType<BrowserProvider["getSigner"]>>>;
};

const Ctx = createContext<EvmWalletContextValue | null>(null);

function normalizeAddress(maybe: unknown): string | null {
  if (typeof maybe !== "string" || !maybe) return null;
  try {
    return getAddress(maybe);
  } catch {
    return maybe.toLowerCase();
  }
}

function isEip1193Provider(p: unknown): p is Eip1193Provider {
  return !!p && typeof p === "object" && typeof (p as Record<string, unknown>).request === "function";
}

type InjectedPreference = "metamask" | "trust" | "coinbase" | null;

function getInjectedEthereum(prefer: InjectedPreference = null): Eip1193Provider | null {
  if (typeof window === "undefined") return null;
  const eth = (window as unknown as { ethereum?: RawInjectedProvider }).ethereum;
  if (!eth) return null;

  // Some environments expose multiple providers. Prefer the requested one.
  if (Array.isArray(eth.providers) && prefer) {
    const match = eth.providers.find((p) => {
      if (!p || typeof p !== "object") return false;
      const pp = p as RawInjectedProvider;
      if (prefer === "metamask") return !!pp.isMetaMask;
      if (prefer === "coinbase") return !!pp.isCoinbaseWallet;
      if (prefer === "trust") return !!pp.isTrust || !!pp.isTrustWallet;
      return false;
    });
    if (isEip1193Provider(match)) return match;
  }

  return isEip1193Provider(eth) ? (eth as Eip1193Provider) : null;
}

const STORAGE_WALLET = "wallet";
const STORAGE_PROVIDER = "walletProvider";

// Bug fix (client-reported "nothing happens" / silent hang on wallet
// connect): connectAsync() has no built-in timeout. If the wallet extension
// doesn't respond - locked, crashed, popup blocked, or just slow - the
// button sits in its loading state forever with no feedback. Wrapping every
// connect call below in a timeout means a failure surfaces within a bounded,
// predictable window instead of leaving the user staring at a spinner.
const CONNECT_TIMEOUT_MS = 20_000;

function withConnectTimeout<T>(promise: Promise<T>, walletLabel: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => {
        reject(
          new Error(
            `${walletLabel} didn't respond in time. Make sure the extension/app is unlocked and try again.`
          )
        );
      }, CONNECT_TIMEOUT_MS);
    }),
  ]);
}

function EvmWalletContextProvider({ children }: { children: React.ReactNode }) {
  const [address, setAddress] = useState<string | null>(null);
  const [providerType, setProviderType] = useState<ProviderType | null>(null);
  const [provider, setProvider] = useState<Eip1193Provider | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { address: wagmiAddress, isConnected, connector } = useAccount();
  const { connectAsync, isPending: isConnecting } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { reconnectAsync } = useReconnect();

  const persistProviderChoice = useCallback((nextProviderType: ProviderType, nextAddress: string | null) => {
    try {
      if (nextAddress) window.localStorage.setItem(STORAGE_WALLET, nextAddress);
      else window.localStorage.removeItem(STORAGE_WALLET);
      window.localStorage.setItem(STORAGE_PROVIDER, nextProviderType);
    } catch {
      // ignore
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await disconnectAsync();
    } catch {
      // ignore
    }
    setAddress(null);
    setProviderType(null);
    setProvider(null);
    try {
      window.localStorage.removeItem(STORAGE_WALLET);
      window.localStorage.removeItem(STORAGE_PROVIDER);
    } catch {
      // ignore
    }
  }, [disconnectAsync]);

  const connectMetaMask = useCallback(async () => {
    setError(null);
    try {
      const result = await withConnectTimeout(
        connectAsync({ connector: metaMaskInjectedConnector }),
        'MetaMask'
      );
      const nextAddr = normalizeAddress(result.accounts[0]);
      setProviderType('injected');
      if (nextAddr) persistProviderChoice('injected', nextAddr);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect');
      throw e;
    }
  }, [connectAsync, persistProviderChoice]);

  const connectTrustWallet = useCallback(async () => {
    setError(null);
    try {
      // Trust Wallet is an injected provider in most environments.
      const result = await withConnectTimeout(
        connectAsync({ connector: injectedConnector }),
        'Trust Wallet'
      );
      const nextAddr = normalizeAddress(result.accounts[0]);
      setProviderType('injected');
      if (nextAddr) persistProviderChoice('injected', nextAddr);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect');
      throw e;
    }
  }, [connectAsync, persistProviderChoice]);

  const connectBifrost = useCallback(async () => {
    setError(null);
    try {
      // Bifrost (and many mobile wallets) expose an injected provider.
      const result = await withConnectTimeout(
        connectAsync({ connector: injectedConnector }),
        'Bifrost'
      );
      const nextAddr = normalizeAddress(result.accounts[0]);
      setProviderType('injected');
      if (nextAddr) persistProviderChoice('injected', nextAddr);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect');
      throw e;
    }
  }, [connectAsync, persistProviderChoice]);

  const connectCoinbase = useCallback(async () => {
    setError(null);
    try {
      const result = await withConnectTimeout(
        connectAsync({ connector: coinbaseConnector }),
        'Coinbase Wallet'
      );
      const nextAddr = normalizeAddress(result.accounts[0]);
      setProviderType('coinbase');
      if (nextAddr) persistProviderChoice('coinbase', nextAddr);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect');
      throw e;
    }
  }, [connectAsync, persistProviderChoice]);

  const connectUniswapWallet = useCallback(async () => {
    setError(null);
    if (!walletConnectConnector) {
      const err = new Error('WalletConnect is not configured. Set NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID.');
      setError(err.message);
      throw err;
    }
    try {
      const result = await withConnectTimeout(
        connectAsync({ connector: walletConnectConnector }),
        'Uniswap Wallet'
      );
      const nextAddr = normalizeAddress(result.accounts[0]);
      // WalletConnect may connect to multiple chains; treat as injected-style for UI.
      setProviderType('injected');
      if (nextAddr) persistProviderChoice('injected', nextAddr);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect');
      throw e;
    }
  }, [connectAsync, persistProviderChoice]);

  const reconnect = useCallback(
    async (opts?: { requestAccountsIfNeeded?: boolean }) => {
      const requestAccountsIfNeeded = Boolean(opts?.requestAccountsIfNeeded);
      try {
        if (isConnected && wagmiAddress) return true;

        // Silent reconnect should not trigger a permission popup.
        const result = await reconnectAsync();
        const nextAddr = normalizeAddress(result?.[0]?.accounts?.[0] || wagmiAddress);
        if (nextAddr) return true;

        // If explicit user action is allowed, request accounts now.
        if (requestAccountsIfNeeded) {
          await connectAsync({ connector: injectedConnector });
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [connectAsync, isConnected, reconnectAsync, wagmiAddress]
  );

  const getSigner = useCallback(async () => {
    let p: Eip1193Provider | null = null;
    let liveAddress: string | null = null;

    // Read wagmi's live config store directly first (not the `connector`/`provider`
    // React state below). Callers that do `await connectX(); await getSigner();`
    // back-to-back (e.g. the KYC flow) can run this before this component has
    // re-rendered with the new `connector`/`provider` values - `wagmiConfig.state`
    // is a plain object on the module-level config, updated synchronously the
    // moment `connectAsync()` resolves, so it's never stale the way a closure
    // captured before the connection can be. This fixes a real bug where the
    // KYC "Verify" button silently failed for anyone without the Coinbase Wallet
    // *browser extension* installed (the old code fell through to scanning
    // `window.ethereum`, which only the extension populates).
    try {
      const state = wagmiConfig.state as unknown as {
        current: string | null;
        connections: Map<
          string,
          { accounts?: readonly string[]; connector?: { getProvider: () => Promise<unknown> } }
        >;
      };
      const activeConnection = state.current ? state.connections.get(state.current) : undefined;
      if (activeConnection?.connector) {
        const cp = (await activeConnection.connector.getProvider()) as unknown;
        if (isEip1193Provider(cp)) {
          p = cp as Eip1193Provider;
          liveAddress = normalizeAddress(activeConnection.accounts?.[0]);
        }
      }
    } catch {
      // ignore - fall through to the hook-derived values below
    }

    // Fall back to the provider from the active wagmi connector (React state).
    // This avoids a common failure mode where `window.ethereum` is a
    // multi-provider shim and signing requests never surface to the wallet
    // the user actually connected.
    if (!p) {
      try {
        if (connector) {
          const cp = (await connector.getProvider()) as unknown;
          if (isEip1193Provider(cp)) p = cp as Eip1193Provider;
        }
      } catch {
        // ignore
      }
    }

    if (!p && provider) p = provider;

    if (!p) {
      const prefer: InjectedPreference =
        providerType === 'coinbase' ? 'coinbase' : 'metamask';
      p = getInjectedEthereum(prefer) || getInjectedEthereum(null);
    }

    if (!p) throw new Error('No wallet provider available.');

    const browserProvider = new BrowserProvider(p as unknown as EthersEip1193Provider);
    // If we know the connected address, request a signer for that address
    // explicitly. This reduces “wrong account” and hanging signer edge cases.
    const resolvedAddress = liveAddress || address;
    if (resolvedAddress) return browserProvider.getSigner(resolvedAddress);
    return browserProvider.getSigner();
  }, [address, connector, provider, providerType]);

  // Keep our local state in sync with wagmi account state.
  useEffect(() => {
    const nextAddr = normalizeAddress(wagmiAddress);
    setAddress(nextAddr);
  }, [wagmiAddress]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!connector) {
          if (!cancelled) setProvider(null);
          return;
        }
        const p = (await connector.getProvider()) as unknown;
        if (!cancelled) setProvider(isEip1193Provider(p) ? (p as Eip1193Provider) : null);
      } catch {
        if (!cancelled) setProvider(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connector]);

  // Best-effort silent reconnect on mount.
  useEffect(() => {
    (async () => {
      try {
        await reconnect({ requestAccountsIfNeeded: false });
      } finally {
        setIsInitialized(true);
      }
    })();
  }, [reconnect]);

  const value = useMemo<EvmWalletContextValue>(
    () => ({
      address,
      isConnected: !!address,
      isConnecting,
      isInitialized,
      error,
      providerType,
      provider,
      connectMetaMask,
      connectTrustWallet,
      connectBifrost,
      connectCoinbase,
      connectUniswapWallet,
      reconnect,
      disconnect,
      getSigner,
    }),
    [
      address,
      connectCoinbase,
      connectMetaMask,
      connectTrustWallet,
      connectBifrost,
      connectUniswapWallet,
      disconnect,
      error,
      getSigner,
      isConnecting,
      isInitialized,
      provider,
      providerType,
      reconnect,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export default function EvmWalletProvider({ children }: { children: React.ReactNode }) {
  return (
    <WagmiProvider config={wagmiConfig}>
      <EvmWalletContextProvider>{children}</EvmWalletContextProvider>
    </WagmiProvider>
  );
}

export function useEvmWallet() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useEvmWallet must be used within EvmWalletProvider");
  return ctx;
}
